import React, { useState, useCallback, useEffect } from "react";
import styled from '@emotion/styled';
import { isMobile } from 'react-device-detect';
import Card from '../components/Ui/Card.jsx';
import { ServerSidePagingTable } from "../components/Ui/Table.jsx";
import { Alert } from "../components/Ui/Alert.jsx";
import { fetchPrivateOrders } from "../http/optician.js";
import Tracking from "../components/Ui/PrivateOrders/Tracking.jsx";
import { formatDateToDDMMYYYY } from "../components/Helpers/dateFns.js";
import { fetchLinkedAccounts } from "../http/account.js";

const ButtonGroup = styled.div`
  display: flex;
  width: 100%;
`;

const Button = styled.button`
  padding: 5px 10px;
  background-color: #007bff;
  color: white;
  font-size: 11px;
  border: none;
  border-radius: 3px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  margin: 0 1px;

  &:hover {
    background-color: #0056b3;
  }

  &:focus {
    outline: none;
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const CenterContainer = styled.div`
  display: flex;
  justify-content: center;
  height: 100vh;
  width: 100%;
`;

const FormContainer = styled.div`
  border-radius: 5px;
  background-color: #fff;
  width: ${isMobile ? '100%' : '50%'};
`;

const Heading = styled.h3`
  margin: 0;
  margin-top: 10px;
  padding-bottom: 5px;
`;

const Paragraph = styled.p`
  margin: 0 0 20px 0;
  font-size:15px;
`;

export default function PrivateOrders({ colour }) {
  const [linkedAccounts, setLinkedAccounts] = useState([]);
  const [privateOrders, setPrivateOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [privateOrderCount, setPrivateOrderCount] = useState(0);
  const [showTracking, setShowTracking] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);

  const [pagingTracker, setPagingTracker] = useState(
    {
      currentPage: 1,
      pageSize: 10,
      filterStatus: null,
      searchValue: null,
      account: null,
      coWorker: null
    });

  useEffect(() => {
    const loadLinkedAccounts = async () => {
      setIsLoading(true);
      try {
        const accounts = await fetchLinkedAccounts();
        const userAccounts = [];

        accounts.forEach(account => {
          userAccounts.push({ label: account.accountdescrip, value: account.accountid });
        });

        setLinkedAccounts(userAccounts);
        setError(null);
      } catch (e) {
        setError(e);
      } finally {
        setIsLoading(false);
      }
    };

    loadLinkedAccounts();
  }, []);

  const loadOrders = useCallback(async (account = null, page, pageSize, filterStatus = null, searchValue = "", coWorker = null) => {
    setIsLoading(true);

    if (account || linkedAccounts) {
      try {
        const fetchedOrders = await fetchPrivateOrders((account ? account : linkedAccounts[0].value), page, pageSize, searchValue);

        if (!fetchedOrders[0].History.length) {
          setPrivateOrders([]);
          setPrivateOrderCount(0);
        }

        if (fetchedOrders && fetchedOrders[0].History) {
          const restructuredOrders = fetchedOrders[0].History.map(order => ({
            ...order,
          }));

          setPrivateOrders(restructuredOrders);
          setPrivateOrderCount(fetchedOrders[0].Count);
        }

        setError(null);
      } catch (e) {
        setError(e);
        setPrivateOrders([]);
          setPrivateOrderCount(0);
      } finally {
        setIsLoading(false);
      }
    }
  }, [linkedAccounts]);

  const columns = [
    {
      Header: "Order Date",
      accessor: row => formatDateToDDMMYYYY(row.PrivateOrder.orderdate),
    },
    {
      Header: "Patient Reference",
      accessor: row => row.PrivateOrder.customername,
    },
    {
      Header: "Prescription Number",
      accessor: row => row.PrivateOrder.barcode,
    },
    {
      width: 300,
      Header: "Actions",
      accessor: "actions",
      Cell: ({ row }) => (
        <ButtonGroup>
          <Button onClick={() => {
            setCurrentOrder(row.original);
            ShowHideTracking();
          }}>
            View Order
          </Button>
        </ButtonGroup>

      )
    },
  ];

  function ShowHideTracking() {
    setShowTracking(!showTracking);
  }

  return (
    <>
      {showTracking &&
        <Tracking
          showHideModal={ShowHideTracking}
          isModalOpen={showTracking}
          colours={colour}
          privateOrder={currentOrder} />}
      <CenterContainer>
        <FormContainer>
          <Card
            label={'Private Order History and Tracking'}
            headercolour={colour.sidebar}
            headerTextColour={colour.font}>
            <Heading>History and Tracking</Heading>
            <Paragraph>To track, and manage your Private Orders, use the table below.</Paragraph>
            <ServerSidePagingTable
              columns={columns}
              pagingFn={loadOrders}
              data={privateOrders}
              recordCount={privateOrderCount}
              setPagingTracker={setPagingTracker}
              arrAccountFilters={linkedAccounts}
            >
            </ServerSidePagingTable>
            {isLoading && <Alert type="info">Loading Private Orders..</Alert>}
            {error && !isLoading && <Alert type="error">Unable to render page due to <b>{error.message}</b></Alert>}
            {!privateOrderCount && !isLoading && <Alert type="information">No Private Orders found.</Alert>}
          </Card>
        </FormContainer>
      </CenterContainer>
    </>
  );
}